import { useEffect, useState } from 'react'
import axios from 'axios'
import { colorDictionary } from '~/utils/colorDictionary'

/**
 * Response shapes for GET /api/furniture/by-sku/{sku} (backend furniture module).
 * All dimensions are in millimetres. See dulap.md/api/docs-json.
 */
interface ZoneFillingResponseDto {
  id: string
  fillingTypeId: string
  minCount: number
  maxCount: number
  defaultCount: number
  paramsOverride: Record<string, unknown> | null
}

interface ZoneDto {
  id: string
  sku: string
  name: string
  label: string | null
  minWidth: number
  maxWidth: number
  defaultWidth: number
  imageUrl: string | null
  fillings: ZoneFillingResponseDto[]
  createdAt: string
  updatedAt: string
}

interface ChipboardSummaryDto {
  id: string
  sku: string | null
  name: string
  decor: string
  thickness: number
  width: number
  height: number
  pricePerSheet: number
  imageUrl: string | null
  normalMapUrl: string | null
}

export interface FurnitureDto {
  id: string
  name: string
  sku: string
  description: string
  minWidth: number
  maxWidth: number
  minHeight: number
  maxHeight: number
  minDepth: number
  maxDepth: number
  photos: string[]
  zones: ZoneDto[]
  chipboards: ChipboardSummaryDto[]
  createdAt: string
  updatedAt: string
}

/**
 * A backend-derived override for the static ProductConstraints. Only carries the
 * fields the backend actually owns: dimension ranges (mm → cm) and the color
 * palette (mapped from chipboards to known color names).
 */
export interface FurnitureConstraintsPatch {
  dimensions: {
    width?: { min: number; max: number }
    height?: { min: number; max: number }
    depth?: { min: number; max: number }
  }
  /** Color names resolved against colorDictionary; empty if nothing matched. */
  colors: string[]
}

const MM_PER_CM = 10
const mmToCm = (mm: number): number => Math.round(mm / MM_PER_CM)

/** A min/max pair is usable only if both are positive and ordered. */
function validRange(min: number, max: number): boolean {
  return (
    Number.isFinite(min) && Number.isFinite(max) && min > 0 && max > 0 && min < max
  )
}

/**
 * Resolve a backend chipboard to a known color name by matching its EGGER decor
 * code (or sku) against the colorDictionary materialCode/fileURL. Returns
 * undefined when no entry matches — unknown decors are dropped, not invented.
 */
function chipboardToColorName(chip: ChipboardSummaryDto): string | undefined {
  const decor = chip.decor?.trim().toUpperCase() ?? ''
  const sku = chip.sku?.trim().toUpperCase() ?? ''
  const name = chip.name?.trim().toLowerCase() ?? ''

  for (const c of colorDictionary) {
    const materialCode = c.materialCode.toUpperCase()
    const fileURL = c.fileURL?.toUpperCase()
    if (decor && materialCode.includes(decor)) return c.name
    if (fileURL && (sku.includes(fileURL) || decor.includes(fileURL)))
      return c.name
    if (name && c.name.toLowerCase() === name) return c.name
  }
  return undefined
}

/**
 * Resolve the backend chipboard id for a selected color name. Matches the same
 * way the palette is built (chipboard decor/sku → colorDictionary name), so the
 * id corresponds to the chipboard the user actually picked. Returns undefined
 * when the furniture has no matching chipboard (e.g. empty chipboard list).
 */
export function findChipboardIdByColorName(
  furniture: FurnitureDto | null,
  colorName: string | undefined
): string | undefined {
  if (!furniture || !colorName) return undefined
  for (const chip of furniture.chipboards ?? []) {
    if (chipboardToColorName(chip) === colorName) return chip.id
  }
  return undefined
}

/**
 * Build a constraints patch from a backend furniture definition. Dimension
 * ranges are converted mm → cm; invalid ranges are omitted so the static
 * defaults survive. Returns null when nothing usable was found.
 */
export function furnitureToConstraintsPatch(
  furniture: FurnitureDto
): FurnitureConstraintsPatch | null {
  const dims: FurnitureConstraintsPatch['dimensions'] = {}
  if (validRange(furniture.minWidth, furniture.maxWidth)) {
    dims.width = { min: mmToCm(furniture.minWidth), max: mmToCm(furniture.maxWidth) }
  }
  if (validRange(furniture.minHeight, furniture.maxHeight)) {
    dims.height = {
      min: mmToCm(furniture.minHeight),
      max: mmToCm(furniture.maxHeight),
    }
  }
  if (validRange(furniture.minDepth, furniture.maxDepth)) {
    dims.depth = { min: mmToCm(furniture.minDepth), max: mmToCm(furniture.maxDepth) }
  }

  const colors: string[] = []
  for (const chip of furniture.chipboards ?? []) {
    const matched = chipboardToColorName(chip)
    if (matched && !colors.includes(matched)) colors.push(matched)
  }

  const hasDims = dims.width || dims.height || dims.depth
  if (!hasDims && colors.length === 0) return null

  return { dimensions: dims, colors }
}

/** Fetch a furniture definition by SKU. Resolves to null on failure. */
async function fetchFurnitureBySku(
  sku: string,
  signal?: AbortSignal
): Promise<FurnitureDto | null> {
  try {
    const { data } = await axios.get<FurnitureDto>(
      `/api/furniture/by-sku/${encodeURIComponent(sku)}`,
      { signal }
    )
    return data
  } catch (err) {
    if (axios.isCancel(err)) return null
    if (import.meta.env.DEV) {
      console.warn(`[furniture] /api/furniture/by-sku/${sku} failed`, err)
    }
    return null
  }
}

/**
 * Process-wide cache of furniture-by-sku promises. The catalog renders many
 * cards sharing a handful of SKUs; without this each card would refetch the same
 * definition. Keyed by SKU; the in-flight promise is shared and reused.
 */
const furnitureCache = new Map<string, Promise<FurnitureDto | null>>()

export function getFurnitureBySkuCached(
  sku: string
): Promise<FurnitureDto | null> {
  let cached = furnitureCache.get(sku)
  if (!cached) {
    cached = fetchFurnitureBySku(sku).then((data) => {
      // Don't cache failures: drop so a later call can retry.
      if (data === null) furnitureCache.delete(sku)
      return data
    })
    furnitureCache.set(sku, cached)
  }
  return cached
}

/**
 * Load the furniture definition for a product on mount / when the SKU changes.
 * Fire-and-forget: the result is held in state but intentionally not surfaced
 * to the UI yet. Aborts the in-flight request if the SKU changes or unmounts.
 */
export function useFurnitureBySku(sku: string | undefined): FurnitureDto | null {
  const [furniture, setFurniture] = useState<FurnitureDto | null>(null)

  useEffect(() => {
    if (!sku) {
      setFurniture(null)
      return
    }

    const controller = new AbortController()
    fetchFurnitureBySku(sku, controller.signal).then((data) => {
      if (!controller.signal.aborted) setFurniture(data)
    })

    return () => controller.abort()
  }, [sku])

  return furniture
}